import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DefaultAvatar } from '../../components/DefaultAvatar';
import { fetchPosts, searchPosts } from '../../services/boardService';
import type { BoardPost } from '../../types/board';
import type { DocumentSnapshot } from 'firebase/firestore';
import { colors } from '../../styles/colors';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase/app';
import { useAuthStore } from '../../stores/authStore';

const prefixColors: Record<string, string> = {
  question: colors.info,
  frequencyShare: colors.primary,
  review: colors.warning,
};

export default function BoardListPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ko' ? 'ko' : 'en';
  const isKo = i18n.language === 'ko';
  const user = useAuthStore((s) => s.user);
  const blockedSet = new Set(user?.blockedUserIds ?? []);

  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [authorInfo, setAuthorInfo] = useState<Record<string, { gender?: string; profileImageUrl?: string }>>({});
  const authorCache = useRef<Record<string, { gender?: string; profileImageUrl?: string }>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'title' | 'content' | 'author'>('title');
  const [isSearching, setIsSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [allPosts, setAllPosts] = useState<BoardPost[]>([]);

  // Load all posts (for client-side pagination)
  const loadAllPosts = useCallback(async () => {
    setLoading(true);
    try {
      // Load up to 200 posts
      const result = await fetchPosts(lang, undefined, 200);
      setAllPosts(result.posts);
      setTotalLoaded(result.posts.length);
      setHasMore(false);
    } catch (err) {
      console.error('[Board] Load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => { loadAllPosts(); }, [lang]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) { setIsSearching(false); loadAllPosts(); return; }
    setIsSearching(true);
    setLoading(true);
    try {
      const results = await searchPosts(lang, searchQuery.trim(), searchType);
      setAllPosts(results);
      setPage(1);
    } catch (err) { console.error('[Board] Search failed:', err); }
    finally { setLoading(false); }
  };

  const formatDate = (ts: any) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    const daysEn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayStr = isKo ? `(${days[d.getDay()]})` : `(${daysEn[d.getDay()]})`;
    return `${d.getFullYear()}.${d.getMonth()+1}.${d.getDate()} ${dayStr}`;
  };

  // Fetch author info (gender, profileImage) for posts without authorGender
  useEffect(() => {
    const missing = allPosts.filter(p => !(p as any).authorGender && p.authorId && !authorCache.current[p.authorId]);
    if (missing.length === 0) return;
    const uids = [...new Set(missing.map(p => p.authorId))];
    Promise.all(uids.map(async uid => {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (snap.exists()) {
          const data = snap.data();
          authorCache.current[uid] = { gender: data.gender, profileImageUrl: data.profileImageUrl };
        }
      } catch {}
    })).then(() => setAuthorInfo({ ...authorCache.current }));
  }, [allPosts]);

  // Separate notices (always on top) from regular posts
  const notices = allPosts.filter((p) => p.isNotice);
  const regularPosts = allPosts.filter((p) => !p.isNotice && !blockedSet.has(p.authorId));
  const totalPages = Math.max(1, Math.ceil(regularPosts.length / pageSize));
  const pagedPosts = regularPosts.slice((page - 1) * pageSize, page * pageSize);
  // Display: notices first, then paged regular posts
  const displayPosts = [...notices, ...pagedPosts];

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <h1 className="text-base font-bold" style={{ color: colors.text }}>{t('board.title')}</h1>
        <button onClick={() => setShowSearch(!showSearch)}
          className="rounded p-1 hover:bg-white/10">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.textMuted} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        </button>
      </div>

      {/* Search bar (collapsible) */}
      {showSearch && <div className="flex flex-col gap-1.5 px-3 py-2" style={{ borderBottom: `1px solid ${colors.border}` }}>
        {/* Search type buttons */}
        <div className="flex gap-1">
          {([
            { key: 'title' as const, label: isKo ? '제목' : 'Title' },
            { key: 'content' as const, label: isKo ? '내용' : 'Content' },
            { key: 'author' as const, label: isKo ? '작성자' : 'Author' },
          ]).map(st => (
            <button key={st.key} onClick={() => setSearchType(st.key)}
              className="rounded px-2 py-0.5 text-[10px] font-bold"
              style={{ backgroundColor: searchType === st.key ? colors.primary : colors.card, color: searchType === st.key ? '#fff' : colors.textMuted }}>
              {st.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5">
        <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`${t('common.search')}...`}
          className="flex-1 rounded-md px-2.5 py-1.5 text-xs outline-none"
          style={{ backgroundColor: colors.card, color: colors.text, border: `1px solid ${colors.border}` }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }} />
        <button onClick={handleSearch} className="rounded-md px-2.5 py-1.5 text-xs font-semibold text-white" style={{ backgroundColor: colors.primary }}>{t('common.search')}</button>
        {isSearching && (
          <button onClick={() => { setSearchQuery(''); setIsSearching(false); loadAllPosts(); }}
            className="rounded-md px-2 py-1.5 text-xs" style={{ backgroundColor: colors.card, color: colors.textMuted }}>✕</button>
        )}
        </div>
      </div>}

      {/* Table header */}
      <div className="flex px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider"
        style={{ backgroundColor: colors.card, color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
        <span className="w-10 shrink-0 text-center">No.</span>
        <span className="flex-1">{isKo ? '제목' : 'Title'}</span>
        <span className="w-16 shrink-0 text-center">{isKo ? '작성자' : 'Author'}</span>
        <span className="w-20 shrink-0 text-center">{isKo ? '작성일' : 'Date'}</span>
        <span className="w-10 shrink-0 text-center">{isKo ? '조회수' : 'Views'}</span>
      </div>

      {/* Post list */}
      <div className="flex-1 overflow-y-auto">
        {displayPosts.length === 0 && !loading ? (
          <p className="mt-12 text-center text-sm" style={{ color: colors.textMuted }}>{t('board.noPost')}</p>
        ) : (
          displayPosts.map((post, idx) => {
            const postNum = post.isNotice ? null : (page - 1) * pageSize + (idx - notices.length) + 1;
            return (
              <Link key={post.id} to={`/board/${post.id}`}
                className="flex items-center px-3 py-2.5 transition-colors hover:bg-white/[0.03]"
                style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: post.isNotice ? `${colors.error}06` : undefined }}>
                <span className="w-10 shrink-0 text-center text-[11px]" style={{ color: post.isNotice ? colors.error : colors.textMuted }}>
                  {post.isNotice ? (
                    <span className="rounded px-1 py-0.5 text-[9px] font-bold text-white" style={{ backgroundColor: colors.error }}>{t('board.notice')}</span>
                  ) : postNum}
                </span>
                <div className="flex-1 overflow-hidden pr-2">
                  <div className="flex items-center gap-1.5">
                    {post.prefix && post.prefix !== 'none' && (
                      <span className="shrink-0 text-[10px] font-semibold" style={{ color: prefixColors[post.prefix] || colors.textMuted }}>[{t(`board.${post.prefix}`)}]</span>
                    )}
                    <span className="truncate text-sm" style={{ color: colors.text, fontWeight: post.isNotice ? 700 : 400 }}>{post.title}</span>
                    {(post.commentCount ?? 0) > 0 && (
                      <span className="shrink-0 text-[10px] font-bold" style={{ color: colors.primary }}>[{post.commentCount}]</span>
                    )}
                  </div>
                </div>
                <span className="flex w-16 shrink-0 items-center justify-center gap-0.5 truncate text-[11px]" style={{ color: post.isNotice ? colors.error : colors.textSecondary }}>
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center overflow-hidden rounded-full" style={{ backgroundColor: '#222', border: `1px solid ${colors.border}` }}>
                    {((post as any).authorProfileImageUrl || authorInfo[post.authorId]?.profileImageUrl)
                      ? <img src={(post as any).authorProfileImageUrl || authorInfo[post.authorId]?.profileImageUrl} alt="" className="h-full w-full object-cover" />
                      : <DefaultAvatar gender={(post as any).authorGender ?? authorInfo[post.authorId]?.gender ?? 'unknown'} size={10} />}
                  </span>
                  <span className="truncate">{post.authorNickname || (post as any).author || '?'}</span>
                </span>
                <span className="w-20 shrink-0 text-center text-[10px]" style={{ color: colors.textMuted }}>{formatDate(post.createdAt)}</span>
                <span className="w-10 shrink-0 text-center text-[11px] font-bold" style={{ color: post.isNotice ? colors.error : colors.textMuted }}>{post.views ?? 0}</span>
              </Link>
            );
          })
        )}
      </div>

      {/* Pagination bar */}
      <div className="flex items-center justify-between px-3 py-2" style={{ borderTop: `1px solid ${colors.border}`, backgroundColor: colors.card }}>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: colors.textMuted }}>{isKo ? '페이지 크기:' : 'Page size:'}</span>
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="rounded px-1.5 py-1 text-xs" style={{ backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}>
            {[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-1">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
            className="rounded px-2 py-1 text-xs disabled:opacity-30" style={{ color: colors.textMuted }}>&lt;</button>
          <span className="text-xs font-bold" style={{ color: colors.text }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            className="rounded px-2 py-1 text-xs disabled:opacity-30" style={{ color: colors.textMuted }}>&gt;</button>
        </div>

        <Link to="/board/write" className="rounded-md px-3 py-1.5 text-xs font-bold text-white" style={{ backgroundColor: colors.primary }}>
          {t('board.write')}
        </Link>
      </div>

      {/* Footer */}
      <div className="py-2 text-center" style={{ backgroundColor: colors.background, borderTop: `1px solid ${colors.border}` }}>
        <p className="text-[10px]" style={{ color: colors.textMuted }}>
          &copy; 2025-{new Date().getFullYear()} <a href="https://ourhertz.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{isKo ? '우리들의 주파수' : 'OurHertz'}</a>. All rights reserved.
        </p>
      </div>
    </div>
  );
}
